import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Badge,
  useColorModeValue,
  Image,
  Icon,
  useToast,
  Skeleton,
  SkeletonText,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  SimpleGrid,
  Flex,
} from '@chakra-ui/react';
import {
  FaArrowLeft,
  FaEuroSign,
  FaBox,
  FaTag,
  FaShoppingCart,
} from 'react-icons/fa';
import ProductCheckoutDrawer from '../components/Merch/ProductCheckoutDrawer';
import ProductAuthRequiredDrawer from '../components/Merch/ProductAuthRequiredDrawer';
import { useAuth } from '../contexts/AuthContext';
import {
  canPurchaseMerch,
  isMerchPreorder,
} from '../utils/merchFulfillmentLabels';

// CIL Colors
const cilColors = {
  yellow: '#ffcc00',
  teal: '#3eb297',
  sky: '#36a9e1',
  pink: '#e95995',
  plum: '#3b235a',
  red: '#e73341',
};

const strapiBaseUrl = process.env.REACT_APP_STRAPI_URL || '';

const parseStrapiMedia = (media) => {
  if (!media) return null;
  const node = media.data?.attributes || media.attributes || media;
  const path = node?.url || node?.formats?.small?.url;
  if (!path) return null;
  return { url: `${strapiBaseUrl}${path}` };
};

// Helper per normalizzare i dati del prodotto
const normalizeProduct = (product) => {
  if (!product) return null;

  if (product.attributes) {
    return {
      id: product.documentId || product.id,
      documentId: product.documentId,
      nome: product.attributes.nome,
      descrizione: product.attributes.descrizione,
      descrizioneBreve: product.attributes.descrizioneBreve,
      disponibilita: product.attributes.disponibilita,
      quantita: product.attributes.quantita,
      prezzo: product.attributes.prezzo,
      visibile: product.attributes.visibile,
      prenotabile: product.attributes.prenotabile === true,
      costoSpedizione: Math.max(
        0,
        parseFloat(product.attributes.costoSpedizione) || 0
      ),
      immaginePrincipale: product.attributes.immaginePrincipale?.data
        ? {
            url: product.attributes.immaginePrincipale.data.attributes?.url
              ? `${process.env.REACT_APP_STRAPI_URL || ''}${product.attributes.immaginePrincipale.data.attributes.url}`
              : product.attributes.immaginePrincipale.data.attributes?.formats
                    ?.small?.url
                ? `${process.env.REACT_APP_STRAPI_URL || ''}${product.attributes.immaginePrincipale.data.attributes.formats.small.url}`
                : null,
          }
        : null,
      immagineTaglie: parseStrapiMedia(product.attributes.immagineTaglie),
      immagini:
        product.attributes.immagini?.data?.map((img) => ({
          url: img.attributes?.url
            ? `${process.env.REACT_APP_STRAPI_URL || ''}${img.attributes.url}`
            : null,
        })) || [],
      tags: product.attributes.tags,
      caratteristiche: product.attributes.caratteristiche,
      sku: product.attributes.sku,
      tipo: product.attributes.tipo,
      nomeBase: product.attributes.nomeBase,
      nomeLinea: product.attributes.nomeLinea,
      taglia: product.attributes.taglia,
      colore: product.attributes.colore,
      lineaKey: product.attributes.lineaKey,
      variantMode: product.attributes.variantMode,
      stockDisponibili:
        product.attributes.stockDisponibili ?? product.attributes.quantita,
    };
  }

  return {
    id: product.id || product.documentId,
    nome: product.nome,
    descrizione: product.descrizione,
    descrizioneBreve: product.descrizioneBreve,
    disponibilita: product.disponibilita,
    quantita: product.quantita,
    prezzo: product.prezzo,
    visibile: product.visibile,
    prenotabile: product.prenotabile === true,
    costoSpedizione: Math.max(0, parseFloat(product.costoSpedizione) || 0),
    immaginePrincipale: product.immaginePrincipale,
    immagineTaglie:
      parseStrapiMedia(product.immagineTaglie) || product.immagineTaglie,
    immagini: product.immagini || [],
    tags: product.tags,
    caratteristiche: product.caratteristiche,
    sku: product.sku,
    tipo: product.tipo,
    nomeBase: product.nomeBase,
    nomeLinea: product.nomeLinea,
    taglia: product.taglia,
    colore: product.colore,
    lineaKey: product.lineaKey,
    variantMode: product.variantMode,
    stockDisponibili: product.stockDisponibili ?? product.quantita,
  };
};

// Helper per ottenere il colore del badge in base alla disponibilità
const getAvailabilityColor = (disponibilita) => {
  const availabilityColors = {
    disponibile: 'green',
    esaurito: 'red',
    'su-ordinazione': 'orange',
    nonDisponibile: 'gray',
  };
  return availabilityColors[disponibilita] || 'gray';
};

// Helper per ottenere il testo della disponibilità
const getAvailabilityText = (disponibilita) => {
  const availabilityTexts = {
    disponibile: 'Disponibile',
    esaurito: 'Esaurito',
    'su-ordinazione': 'Su Ordinazione',
    nonDisponibile: 'Non Disponibile',
  };
  return availabilityTexts[disponibilita] || 'Non Disponibile';
};

const MERCH_COLOR_HEX = {
  NERO: '#1a1a1a',
  BIANCO: '#ffffff',
  GRIGIO: '#9ca3af',
  ROSSO: '#dc2626',
  VERDE: '#16a34a',
  BLU: '#2563eb',
  AZZURRO: '#38bdf8',
  GIALLO: '#eab308',
  ARANCIONE: '#ea580c',
  ROSA: '#ec4899',
  FUCSIA: '#d946ef',
  VIOLA: '#7c3aed',
  MARRONE: '#78350f',
  BEIGE: '#d4c4a8',
};

function getProductDisplayTitle(product) {
  if (!product) return '';
  if (product.nomeLinea?.trim()) return product.nomeLinea.trim();

  let title = (product.nome || '').trim();
  if (product.colore && title.endsWith(` - ${product.colore}`)) {
    title = title.slice(0, -` - ${product.colore}`.length).trim();
  }
  if (product.taglia && title.endsWith(` - ${product.taglia}`)) {
    title = title.slice(0, -` - ${product.taglia}`.length).trim();
  }
  return title;
}

function groupSupportProductsByColor(products, { excludeLineaKey } = {}) {
  const groups = new Map();

  products.forEach((item) => {
    if (!item || item.visibile === false) return;
    if (excludeLineaKey && item.lineaKey === excludeLineaKey) return;

    const groupKey = item.colore
      ? `${item.lineaKey || item.id}::${item.colore}`
      : String(item.lineaKey || item.id);

    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
    }
    groups.get(groupKey).push(item);
  });

  return Array.from(groups.values()).map((variants) => {
    const inStock = variants.find(
      (variant) => (variant.stockDisponibili ?? variant.quantita ?? 0) > 0
    );
    return inStock || variants[0];
  });
}

function formatDisplayColor(colore) {
  if (!colore) return '';
  return String(colore)
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getSupportProductTitle(product) {
  const base = getProductDisplayTitle(product);
  if (product?.colore) {
    return `${base} — ${formatDisplayColor(product.colore)}`;
  }
  return base;
}

function getImageUrl(img) {
  if (!img) return null;
  if (typeof img === 'string') return img;
  return img.url || null;
}

function buildProductImages(product) {
  if (!product) return [];

  const images = [];
  const seen = new Set();

  const addImage = (img) => {
    const url = getImageUrl(img);
    if (!url || seen.has(url)) return;
    seen.add(url);
    images.push(typeof img === 'string' ? { url: img } : img);
  };

  addImage(product.immaginePrincipale);
  (product.immagini || []).forEach(addImage);

  const taglieMedia =
    parseStrapiMedia(product.immagineTaglie) || product.immagineTaglie;
  const taglieUrl = getImageUrl(taglieMedia);
  if (product.tipo === 'TSH' && taglieUrl) {
    addImage({ url: taglieUrl, isTaglieGuide: true });
  }

  return images;
}

// Helper per normalizzare un ID (converte in stringa per il confronto)
const normalizeId = (id) => {
  if (id === null || id === undefined) return null;
  return String(id);
};

// Helper per calcolare lo sconto applicabile a un prodotto
const getProductDiscount = (productId, discounts) => {
  if (!discounts || discounts.length === 0 || !productId) return null;

  const normalizedProductId = normalizeId(productId);

  const applicableDiscounts = discounts.filter((discount) => {
    // Se lo sconto ha type = 'all-products', si applica a tutti i prodotti
    if (discount.type === 'all-products') {
      return true;
    }
    // Se lo sconto ha type = 'products', verifica se si applica a questo prodotto specifico
    if (
      discount.type === 'products' &&
      discount.productIds &&
      Array.isArray(discount.productIds)
    ) {
      return discount.productIds.some(
        (id) => normalizeId(id) === normalizedProductId
      );
    }
    return false;
  });

  if (applicableDiscounts.length === 0) return null;

  // Restituisci lo sconto con la percentuale più alta
  return applicableDiscounts.reduce((best, current) =>
    current.percentageAmount > best.percentageAmount ? current : best
  );
};

// Helper per calcolare il prezzo scontato
const calculateDiscountedPrice = (originalPrice, discountPercentage) => {
  if (!discountPercentage || discountPercentage <= 0) return originalPrice;
  return originalPrice * (1 - discountPercentage / 100);
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userData, isAuthenticated } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [isAuthRequiredOpen, setIsAuthRequiredOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [availableDiscounts, setAvailableDiscounts] = useState([]);
  const [otherProducts, setOtherProducts] = useState([]);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [lineVariants, setLineVariants] = useState([]);
  const [selectedColore, setSelectedColore] = useState('');
  const [selectedTaglia, setSelectedTaglia] = useState('');

  const toast = useToast();

  const bgColor = useColorModeValue('white', 'gray.800');
  const cardBgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // ── Load available discounts ──────────────────────────────────────────
  useEffect(() => {
    const fetchDiscounts = async () => {
      if (!userData) {
        setAvailableDiscounts([]);
        return;
      }

      try {
        const response = await fetch('/api/user/discounts');
        if (response.ok) {
          const data = await response.json();
          setAvailableDiscounts(data.discounts || []);
        }
      } catch (err) {
        console.error('Errore nel caricamento degli sconti:', err);
        setAvailableDiscounts([]);
      }
    };

    fetchDiscounts();
  }, [userData]);

  // ── Load other ways to support ────────────────────────────────────────
  useEffect(() => {
    const fetchSupportData = async () => {
      try {
        // Fetch altri prodotti (escludendo quello corrente)
        const productsResponse = await fetch(
          '/api/products/catalog?populate=*'
        );
        if (productsResponse.ok) {
          const productsData = await productsResponse.json();
          const allProducts = productsData.products || productsData.data || [];
          const filteredProducts = groupSupportProductsByColor(
            allProducts.map(normalizeProduct).filter(Boolean),
            { excludeLineaKey: product?.lineaKey }
          ).slice(0, 3);
          setOtherProducts(filteredProducts);
        }
      } catch (err) {
        console.error('Errore nel caricamento dei dati di supporto:', err);
      }
    };

    if (product) {
      fetchSupportData();
    }
  }, [product]);

  // ── Carousel auto-scroll loop ────────────────────────────────────────
  useEffect(() => {
    if (otherProducts.length <= 4) {
      return undefined;
    }

    const interval = setInterval(() => {
      setCarouselIndex((prevIndex) => {
        const nextIndex = prevIndex + 4;
        // Se abbiamo raggiunto la fine, torna all'inizio senza animazione
        if (nextIndex >= otherProducts.length) {
          // Reset immediato senza transizione per creare l'effetto loop continuo
          setTimeout(() => {
            setCarouselIndex(0);
          }, 600); // Dopo la fine della transizione
          return prevIndex; // Mantieni la posizione corrente durante il reset
        }
        return nextIndex;
      });
    }, 4000); // Cambia slide ogni 4 secondi

    return () => clearInterval(interval);
  }, [otherProducts]);

  // Fetch prodotto da Strapi
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/products/${id}?populate=*`);
        if (!response.ok) {
          throw new Error('Errore nel recupero del prodotto');
        }
        const data = await response.json();

        const productData = data.product || data.data;
        if (!productData) {
          throw new Error('Prodotto non trovato');
        }

        const normalizedProduct = normalizeProduct(productData);
        setProduct(normalizedProduct);
        setSelectedColore(normalizedProduct.colore || '');
        setSelectedTaglia(normalizedProduct.taglia || '');
      } catch (err) {
        console.error('Error fetching product:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProduct();
    }
  }, [id]);

  useEffect(() => {
    const fetchVariants = async () => {
      if (!product?.lineaKey) {
        setLineVariants([]);
        return;
      }
      try {
        const response = await fetch(
          `/api/products/line/${encodeURIComponent(product.lineaKey)}/variants`
        );
        if (response.ok) {
          const data = await response.json();
          setLineVariants(data.variants || []);
        }
      } catch (err) {
        console.error('Errore caricamento varianti:', err);
      }
    };
    fetchVariants();
  }, [product?.lineaKey]);

  const availableTaglie = useMemo(() => {
    const filtered = selectedColore
      ? lineVariants.filter((v) => v.colore === selectedColore)
      : lineVariants;
    const values = new Set(filtered.map((v) => v.taglia).filter(Boolean));
    return Array.from(values);
  }, [lineVariants, selectedColore]);

  const availableColori = useMemo(() => {
    const filtered = selectedTaglia
      ? lineVariants.filter((v) => v.taglia === selectedTaglia)
      : lineVariants;
    const values = new Set(filtered.map((v) => v.colore).filter(Boolean));
    return Array.from(values);
  }, [lineVariants, selectedTaglia]);

  const resolvedVariant = useMemo(() => {
    if (lineVariants.length === 0) return product;
    if (product?.variantMode === 'none') return product;

    const match = lineVariants.find((variant) => {
      if (selectedTaglia && variant.taglia !== selectedTaglia) return false;
      if (selectedColore && variant.colore !== selectedColore) return false;
      return true;
    });

    return match || product;
  }, [lineVariants, product, selectedTaglia, selectedColore]);

  const activeProduct = resolvedVariant || product;

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [activeProduct?.id]);

  useEffect(() => {
    if (!resolvedVariant?.id || resolvedVariant.id === product?.id) return;

    setProduct(resolvedVariant);
    setSelectedColore(resolvedVariant.colore || '');
    setSelectedTaglia(resolvedVariant.taglia || '');

    const nextPath = `/merch/product/${resolvedVariant.id}`;
    if (window.location.pathname !== nextPath) {
      window.history.replaceState(window.history.state, '', nextPath);
    }
  }, [resolvedVariant, product?.id]);

  const handleBuyClick = () => {
    if (activeProduct.disponibilita !== 'disponibile') {
      toast({
        title: 'Prodotto non disponibile',
        description:
          "Questo prodotto non è attualmente disponibile per l'acquisto",
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!isAuthenticated) {
      setIsAuthRequiredOpen(true);
      return;
    }

    setIsCheckoutOpen(true);
  };

  const handleCheckoutSuccess = (summary) => {
    setIsCheckoutOpen(false);
    navigate('/merch/ordine-confermato', { state: summary });
  };

  if (loading) {
    return (
      <Box bg={bgColor} minH="100vh" py={8}>
        <Container maxW="container.xl">
          <VStack spacing={8}>
            <Skeleton height="40px" width="200px" />
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} width="100%">
              <Box width="100%" aspectRatio={1}>
                <Skeleton width="100%" height="100%" />
              </Box>
              <VStack spacing={4} align="stretch">
                <Skeleton height="40px" />
                <SkeletonText noOfLines={4} />
                <Skeleton height="60px" />
              </VStack>
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>
    );
  }

  if (error || !product) {
    return (
      <Box bg={bgColor} minH="100vh" py={8}>
        <Container maxW="container.xl">
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            <AlertTitle>Errore!</AlertTitle>
            <AlertDescription>
              {error || 'Prodotto non trovato'}
            </AlertDescription>
          </Alert>
          <Button
            leftIcon={<FaArrowLeft />}
            mt={4}
            onClick={() => navigate('/merch')}
            colorScheme="blue"
          >
            Torna al Merch
          </Button>
        </Container>
      </Box>
    );
  }

  const allImages = buildProductImages(activeProduct);
  const currentImage = allImages[currentImageIndex];

  const mainImageUrl =
    getImageUrl(currentImage) || getImageUrl(activeProduct.immaginePrincipale);

  const productStock =
    activeProduct.stockDisponibili ?? activeProduct.quantita ?? 0;

  const isPreorder = isMerchPreorder(activeProduct, 1);
  const canPurchase = canPurchaseMerch(activeProduct, 1);

  // Calcola lo sconto applicabile
  const productId = activeProduct.documentId || activeProduct.id;
  const discount = getProductDiscount(productId, availableDiscounts);
  const hasDiscount = discount && discount.percentageAmount > 0;
  const discountedPrice = discount
    ? calculateDiscountedPrice(activeProduct.prezzo, discount.percentageAmount)
    : activeProduct.prezzo;

  const displayTitle = getProductDisplayTitle(product);

  const hasProductTags = Array.isArray(product.tags) && product.tags.length > 0;
  const caratteristicheEntries =
    product.caratteristiche && typeof product.caratteristiche === 'object'
      ? Object.entries(product.caratteristiche)
      : null;
  const hasCaratteristiche =
    (caratteristicheEntries && caratteristicheEntries.length > 0) ||
    (product.caratteristiche &&
      typeof product.caratteristiche !== 'object' &&
      String(product.caratteristiche).trim().length > 0);
  const hasVariants = availableColori.length > 1 || availableTaglie.length > 1;

  return (
    <Box bg={bgColor} minH="100vh" py={8}>
      <Container maxW="container.xl">
        {/* Back Button */}
        <Button
          leftIcon={<FaArrowLeft />}
          mb={6}
          onClick={() => navigate('/merch')}
          variant="ghost"
          colorScheme="blue"
        >
          Torna al Merch
        </Button>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
          {/* Immagini */}
          <Box>
            <VStack spacing={4}>
              {/* Immagine Principale */}
              <Box
                position="relative"
                borderRadius="lg"
                overflow="hidden"
                border="1px"
                borderColor={borderColor}
                width="100%"
                aspectRatio={1}
              >
                {/* Badge sconto */}
                {hasDiscount && (
                  <Badge
                    position="absolute"
                    top={4}
                    left={4}
                    bg={cilColors.yellow}
                    color={cilColors.plum}
                    fontSize="lg"
                    fontWeight="bold"
                    px={3}
                    py={2}
                    borderRadius="md"
                    zIndex={2}
                    boxShadow="0 2px 8px rgba(0, 0, 0, 0.2)"
                  >
                    -{discount.percentageAmount}%
                  </Badge>
                )}
                {mainImageUrl ? (
                  <Image
                    src={mainImageUrl}
                    alt={
                      currentImage?.isTaglieGuide
                        ? 'Guida taglie'
                        : displayTitle
                    }
                    width="100%"
                    height="100%"
                    objectFit="contain"
                    fallbackSrc="https://via.placeholder.com/400x400?text=Immagine+Non+Disponibile"
                  />
                ) : (
                  <Box
                    bg="gray.100"
                    width="100%"
                    height="100%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Icon as={FaBox} size="4xl" color="gray.400" />
                  </Box>
                )}
              </Box>

              {/* Galleria Immagini */}
              {allImages.length > 1 && (
                <SimpleGrid columns={4} spacing={2} width="100%">
                  {allImages.map((img, index) => {
                    const imgUrl = getImageUrl(img);
                    const imgKey =
                      imgUrl || `gallery-img-${product.id}-${index}`;
                    return (
                      <Box
                        key={`gallery-${product.id}-${imgKey}`}
                        borderRadius="md"
                        overflow="hidden"
                        border="2px"
                        borderColor={
                          currentImageIndex === index
                            ? cilColors.sky
                            : borderColor
                        }
                        cursor="pointer"
                        onClick={() => setCurrentImageIndex(index)}
                        _hover={{ borderColor: cilColors.sky }}
                        aspectRatio={1}
                      >
                        {imgUrl ? (
                          <Image
                            src={imgUrl}
                            alt={
                              img.isTaglieGuide
                                ? 'Guida taglie'
                                : `${displayTitle} - Immagine ${index + 1}`
                            }
                            width="100%"
                            height="100%"
                            objectFit="contain"
                          />
                        ) : (
                          <Box
                            bg="gray.100"
                            width="100%"
                            height="100%"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Icon as={FaBox} size="md" color="gray.400" />
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </SimpleGrid>
              )}
            </VStack>
          </Box>

          {/* Informazioni Prodotto */}
          <Box position={{ md: 'sticky' }} top={{ md: 6 }} alignSelf="start">
            <VStack spacing={5} align="stretch">
              <Box>
                <Flex gap={2} flexWrap="wrap" mb={3}>
                  <Badge
                    colorScheme={
                      isPreorder
                        ? 'orange'
                        : getAvailabilityColor(activeProduct.disponibilita)
                    }
                    variant="solid"
                    fontSize="xs"
                    px={2}
                    py={0.5}
                  >
                    {isPreorder
                      ? 'Prenotabile'
                      : getAvailabilityText(activeProduct.disponibilita)}
                  </Badge>
                  {hasDiscount && (
                    <Badge
                      bg={cilColors.yellow}
                      color={cilColors.plum}
                      fontSize="xs"
                      px={2}
                      py={0.5}
                    >
                      -{discount.percentageAmount}%
                    </Badge>
                  )}
                </Flex>

                <Heading
                  size="lg"
                  color={cilColors.plum}
                  lineHeight="short"
                  noOfLines={3}
                >
                  {displayTitle}
                </Heading>

                {product.descrizioneBreve && (
                  <Text fontSize="md" color="gray.600" mt={2} lineHeight="tall">
                    {product.descrizioneBreve}
                  </Text>
                )}
              </Box>

              {activeProduct.prezzo !== undefined && (
                <HStack spacing={3} align="baseline" flexWrap="wrap">
                  <Text
                    fontSize="3xl"
                    fontWeight="bold"
                    color={hasDiscount ? 'green.600' : cilColors.teal}
                    lineHeight="1"
                  >
                    € {parseFloat(discountedPrice).toFixed(2)}
                  </Text>
                  {hasDiscount && (
                    <Text
                      fontSize="lg"
                      color="gray.400"
                      textDecoration="line-through"
                    >
                      € {parseFloat(activeProduct.prezzo).toFixed(2)}
                    </Text>
                  )}
                </HStack>
              )}

              <Box
                p={4}
                bg={cardBgColor}
                borderRadius="lg"
                border="1px"
                borderColor={borderColor}
              >
                <VStack spacing={4} align="stretch">
                  {hasVariants && (
                    <VStack spacing={4} align="stretch">
                      {availableColori.length > 1 && (
                        <Box>
                          <Text fontSize="sm" fontWeight="semibold" mb={2}>
                            Colore
                          </Text>
                          <Flex gap={2} flexWrap="wrap">
                            {availableColori.map((colore) => {
                              const isSelected = selectedColore === colore;
                              const swatchHex =
                                MERCH_COLOR_HEX[
                                  String(colore).trim().toUpperCase()
                                ] || null;

                              return (
                                <Button
                                  key={colore}
                                  size="sm"
                                  variant="outline"
                                  borderWidth="2px"
                                  borderColor={
                                    isSelected ? cilColors.sky : borderColor
                                  }
                                  bg={isSelected ? 'blue.50' : 'transparent'}
                                  fontWeight={
                                    isSelected ? 'semibold' : 'normal'
                                  }
                                  onClick={() => setSelectedColore(colore)}
                                  _hover={{ borderColor: cilColors.sky }}
                                >
                                  <HStack spacing={2}>
                                    {swatchHex && (
                                      <Box
                                        w="14px"
                                        h="14px"
                                        borderRadius="full"
                                        bg={swatchHex}
                                        border="1px solid"
                                        borderColor="gray.300"
                                        flexShrink={0}
                                      />
                                    )}
                                    <Text fontSize="sm">{colore}</Text>
                                  </HStack>
                                </Button>
                              );
                            })}
                          </Flex>
                        </Box>
                      )}

                      {availableTaglie.length > 1 && (
                        <Box>
                          <Text fontSize="sm" fontWeight="semibold" mb={2}>
                            Taglia
                          </Text>
                          <Flex gap={2} flexWrap="wrap">
                            {availableTaglie.map((taglia) => {
                              const isSelected = selectedTaglia === taglia;

                              return (
                                <Button
                                  key={taglia}
                                  size="sm"
                                  minW="44px"
                                  variant="outline"
                                  borderWidth="2px"
                                  borderColor={
                                    isSelected ? cilColors.sky : borderColor
                                  }
                                  bg={isSelected ? 'blue.50' : 'transparent'}
                                  fontWeight={
                                    isSelected ? 'semibold' : 'normal'
                                  }
                                  onClick={() => setSelectedTaglia(taglia)}
                                  _hover={{ borderColor: cilColors.sky }}
                                >
                                  {taglia}
                                </Button>
                              );
                            })}
                          </Flex>
                        </Box>
                      )}
                    </VStack>
                  )}

                  {productStock !== undefined && (
                    <HStack spacing={2} fontSize="sm" color="gray.600">
                      <Icon as={FaBox} color="gray.400" />
                      <Text>
                        {isPreorder ? (
                          <>
                            Disponibilità:{' '}
                            <Text as="span" fontWeight="semibold">
                              su prenotazione
                            </Text>
                          </>
                        ) : (
                          <>
                            Disponibili:{' '}
                            <Text as="span" fontWeight="semibold">
                              {productStock}
                            </Text>
                          </>
                        )}
                      </Text>
                    </HStack>
                  )}

                  {isPreorder && (
                    <Alert status="info" borderRadius="md" py={2}>
                      <AlertIcon boxSize={4} />
                      <Text fontSize="sm">
                        Prenotazione a stock zero: conferma l&apos;ordine e lo
                        metteremo subito in produzione nel prossimo ordine!
                      </Text>
                    </Alert>
                  )}

                  <Button
                    leftIcon={<FaShoppingCart />}
                    size="lg"
                    colorScheme="green"
                    onClick={handleBuyClick}
                    isDisabled={!canPurchase}
                    width="100%"
                  >
                    {canPurchase
                      ? isPreorder
                        ? 'Prenota ora'
                        : 'Acquista ora'
                      : 'Non disponibile'}
                  </Button>

                  {!canPurchase && (
                    <Alert status="warning" borderRadius="md" py={2}>
                      <AlertIcon boxSize={4} />
                      <Text fontSize="sm">
                        {activeProduct.disponibilita === 'esaurito' &&
                        !activeProduct.prenotabile
                          ? 'Questo prodotto è attualmente esaurito'
                          : activeProduct.disponibilita === 'su-ordinazione'
                            ? 'Disponibile su ordinazione'
                            : "Non disponibile per l'acquisto"}
                      </Text>
                    </Alert>
                  )}
                </VStack>
              </Box>

              {(hasProductTags || activeProduct.sku) && (
                <Flex
                  gap={3}
                  flexWrap="wrap"
                  align="center"
                  pt={1}
                  borderTop="1px"
                  borderColor={borderColor}
                >
                  {hasProductTags &&
                    product.tags.map((tag) => (
                      <Badge
                        key={`tag-${product.id}-${tag}`}
                        colorScheme="teal"
                        variant="subtle"
                        fontSize="xs"
                        px={2}
                        py={0.5}
                      >
                        <Icon as={FaTag} mr={1} boxSize={2.5} />
                        {tag}
                      </Badge>
                    ))}
                  {activeProduct.sku && (
                    <Text fontSize="xs" color="gray.500" ml="auto">
                      SKU {activeProduct.sku}
                    </Text>
                  )}
                </Flex>
              )}

              {hasCaratteristiche && (
                <Box>
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color="gray.500"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    mb={2}
                  >
                    Dettagli prodotto
                  </Text>
                  {caratteristicheEntries ? (
                    <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2}>
                      {caratteristicheEntries.map(([key, value]) => (
                        <Box
                          key={key}
                          p={2.5}
                          bg={cardBgColor}
                          borderRadius="md"
                          border="1px"
                          borderColor={borderColor}
                        >
                          <Text fontSize="xs" color="gray.500">
                            {key}
                          </Text>
                          <Text fontSize="sm" fontWeight="medium">
                            {String(value)}
                          </Text>
                        </Box>
                      ))}
                    </SimpleGrid>
                  ) : (
                    <Text fontSize="sm" color="gray.600">
                      {String(product.caratteristiche)}
                    </Text>
                  )}
                </Box>
              )}

              {product.descrizione && (
                <Box>
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color="gray.500"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    mb={2}
                  >
                    Descrizione
                  </Text>
                  <Text
                    fontSize="sm"
                    color="gray.600"
                    lineHeight="tall"
                    whiteSpace="pre-line"
                  >
                    {product.descrizione}
                  </Text>
                </Box>
              )}
            </VStack>
          </Box>
        </SimpleGrid>

        {/* Altri modi per sostenerci - Carosello */}
        {otherProducts.length > 0 &&
          (() => {
            const totalItems = otherProducts.length;

            // Duplica gli elementi per creare un loop infinito
            const duplicatedItems = [
              ...otherProducts,
              ...otherProducts,
              ...otherProducts,
            ];
            const currentOffset = carouselIndex % totalItems;

            return (
              <Box mt={12}>
                <Heading
                  size="lg"
                  color={cilColors.plum}
                  mb={6}
                  textAlign="center"
                >
                  Altri modi per sostenerci
                </Heading>

                <Box position="relative" overflow="hidden" width="100%">
                  <Box
                    display="flex"
                    gap={6}
                    transition="transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)"
                    transform={{
                      base: `translateX(calc(-${currentOffset} * 100%))`,
                      md: `translateX(calc(-${currentOffset} * (25% + 1.5rem)))`,
                    }}
                    w="full"
                  >
                    {duplicatedItems.map((prod) => {
                      const prodImageUrl =
                        prod.immaginePrincipale?.url ||
                        (typeof prod.immaginePrincipale === 'string'
                          ? prod.immaginePrincipale
                          : null);
                      return (
                        <Box
                          key={`product-${prod.lineaKey || prod.id}-${prod.colore || 'default'}`}
                          w={{ base: '100%', md: 'calc(25% - 18px)' }}
                          flexShrink={0}
                          bg={cardBgColor}
                          borderRadius="lg"
                          border="1px"
                          borderColor={borderColor}
                          overflow="hidden"
                          transition="all 0.3s"
                          _hover={{
                            transform: 'translateY(-4px)',
                            shadow: 'xl',
                            borderColor: cilColors.sky,
                          }}
                        >
                          {prodImageUrl && (
                            <Box
                              width="100%"
                              aspectRatio={1}
                              overflow="hidden"
                              bg="gray.50"
                            >
                              <Image
                                src={prodImageUrl}
                                alt={getSupportProductTitle(prod)}
                                width="100%"
                                height="100%"
                                objectFit="contain"
                              />
                            </Box>
                          )}
                          <Box p={4}>
                            <VStack spacing={3} align="stretch">
                              <Heading
                                size="md"
                                color={cilColors.plum}
                                noOfLines={2}
                              >
                                {getSupportProductTitle(prod)}
                              </Heading>
                              {prod.descrizioneBreve && (
                                <Text
                                  fontSize="sm"
                                  color="gray.600"
                                  noOfLines={3}
                                >
                                  {prod.descrizioneBreve}
                                </Text>
                              )}
                              {prod.prezzo !== undefined && (
                                <HStack spacing={1} align="center">
                                  <Icon
                                    as={FaEuroSign}
                                    color={cilColors.teal}
                                    size="sm"
                                  />
                                  <Text
                                    fontSize="lg"
                                    fontWeight="semibold"
                                    color={cilColors.teal}
                                  >
                                    {parseFloat(prod.prezzo).toFixed(2)}
                                  </Text>
                                </HStack>
                              )}
                              <Button
                                colorScheme="blue"
                                size="sm"
                                onClick={() =>
                                  navigate(`/merch/product/${prod.id}`)
                                }
                                width="100%"
                              >
                                Vedi Dettagli
                              </Button>
                            </VStack>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Indicatori di posizione */}
                  {totalItems > 4 && (
                    <HStack spacing={2} justify="center" mt={4}>
                      {Array.from(
                        { length: Math.ceil(totalItems / 4) },
                        (_, idx) => idx
                      ).map((idx) => (
                        <Box
                          key={`carousel-indicator-${idx}`}
                          w={2}
                          h={2}
                          borderRadius="full"
                          bg={
                            Math.floor(carouselIndex / 4) === idx
                              ? cilColors.plum
                              : 'gray.300'
                          }
                          cursor="pointer"
                          onClick={() => setCarouselIndex(idx * 4)}
                          transition="all 0.2s"
                          _hover={{ bg: cilColors.plum, opacity: 0.7 }}
                        />
                      ))}
                    </HStack>
                  )}
                </Box>
              </Box>
            );
          })()}

        {/* Checkout Drawer */}
        {isAuthRequiredOpen && (
          <ProductAuthRequiredDrawer
            isOpen={isAuthRequiredOpen}
            onClose={() => setIsAuthRequiredOpen(false)}
            product={activeProduct}
          />
        )}

        {isCheckoutOpen && (
          <ProductCheckoutDrawer
            isOpen={isCheckoutOpen}
            onClose={() => setIsCheckoutOpen(false)}
            product={activeProduct}
            onSuccess={handleCheckoutSuccess}
          />
        )}
      </Container>
    </Box>
  );
};

export default ProductDetail;
